import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import StripeElement from "./StripeElement";
import SidebarFooter from "./SidebarFooter";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { useContext, useEffect, useState } from "react";
import { ToastContext } from "../contexts/ToastContext/ToastContext";
import { useDispatch } from "react-redux";
import { AnnualPlanService } from "../services/annualPlan.service";
import { updatePaymentMethod } from "../redux/slices/annualPlanSlice";
import { ToastVariants } from "../models";
import { formatCurrency, getPlanDetails } from "../utils";

const PaymentForm = ({
  handleCancel,
  officeId,
  userId,
  userAnnualPlanId,
}: {
  officeId: string;
  userId: string;
  userAnnualPlanId: string;
  handleCancel: (val: boolean) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const submit = async () => {
    if (stripe && elements) {
      setLoading(true);
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (result.error) {
        setLoading(false);
        toast?.errorToast(result.error?.message ?? "Payment failed");
        handleCancel(false);
        return;
      }
      if (result.setupIntent.payment_method) {
        await dispatch(
          updatePaymentMethod({
            paymentMethodId: result.setupIntent.payment_method as string,
            officeId,
            userId,
            userAnnualPlanId,
          })
        );
        toast?.openToast("Payment method updated successfully", 2000, ToastVariants.Success);
      }
      handleCancel(false);
      setLoading(false);
    }
  };
  return (
    <>
      <p className="py-2 text-semibold">
        <b>Credit Card Information</b>
      </p>

      <PaymentElement
        className="stripe-payment-element"
        id={"Payment-Form-Stripe"}
        onChange={(e: any) => {
          setIsDisabled(e.complete);
        }}
      />
      <SidebarFooter
        cancelText={"Cancel"}
        saveText={"Submit"}
        loading={loading}
        isDisabledPrev={loading}
        isDisabledNext={!isDisabled || loading}
        handleClose={handleCancel}
        handleSave={submit}
      />
    </>
  );
};

const UpdatePaymentMethod = ({
  handleCancel,
  officeId,
  userId,
  userAnnualPlanId,
}: {
  officeId: string;
  userId: string;
  userAnnualPlanId: string;
  handleCancel: (val: boolean) => void;
}) => {
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const { cardDetails } = useSelector((state: RootState) => state.annualPlan);
  const currentOffice = currentUser?.offices?.length ? currentUser?.offices[0] : undefined;
  const currentPlan = currentUser?.userAnnualPlan?.officeAnnualPlan ?? undefined;
  const planDetails = getPlanDetails(currentPlan);
  const price = currentPlan?.subscription?.price ?? 0;
  const docnoteVal = price * 0.1;
  const subtotal = price + docnoteVal;
  const total = price && currentOffice?.taxEnabled ? subtotal + subtotal * 0.13 : subtotal;

  const fetchSecret = async () => {
    try {
      const res: any = await AnnualPlanService.setupIntent(userId, officeId);
      setSecret(res.clientSecret);
      setLoading(false);
    } catch (_) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSecret();
  }, []);
  const SelectedPlanText = planDetails?.subscriptionType === "monthly" ? "monthly for a term of twelve months" : "once for a term of twelve months";

  return (
    <div className="px-8 relative">
      <p>
        This subscription will be charged {SelectedPlanText} to the below credit card. Please note that this subscription cannot be cancelled within
        the 12-month term. You may update your payment method at any time.
      </p>
      <div className="flex flex-col gap-3 my-3">
        <div className="flex">
          <p className="w-44">Annual Plan:</p> <p>{planDetails?.planName}</p>
        </div>
        <div className="flex">
          <p className="w-44 capitalize">{planDetails?.subscriptionType} Fee:</p> <p>{planDetails?.price}</p>
        </div>
        <div className="flex">
          <p className="w-44 capitalize">Docnote Fee (10%):</p> <p>{formatCurrency(((price * 0.1) / 100).toString(), "blur")}</p>
        </div>
        {currentOffice?.taxEnabled ? (
          <div className="flex">
            <p className="w-44 capitalize">HST (13%):</p> <p>{formatCurrency((((price + docnoteVal) * 0.13) / 100).toString(), "blur")}</p>
          </div>
        ) : null}
        <div className="flex">
          <p className="w-44 font-bold">Total {planDetails?.subscriptionType} Fee:</p> <p>{formatCurrency((total / 100).toString(), "blur")}</p>
        </div>
      </div>
      <div className="my-3">
        <h2 className="uppercase text-docsigna-purple font-bold">Current Method:</h2>
        <div className="flex flex-wrap justify-between">
          <div className="flex flex-col gap-2">
            <p>Credit Card Number</p>
            <p>**** **** **** {cardDetails?.card4Digits}</p>
          </div>
          <div className="flex flex-col gap-2">
            <p>Expiry Date</p>
            <p>{cardDetails?.cardExpirationDate}</p>
          </div>
        </div>
      </div>
      {!loading ? (
        <StripeElement clientSecret={secret}>
          <PaymentForm officeId={officeId} userId={userId} userAnnualPlanId={userAnnualPlanId} handleCancel={handleCancel} />
        </StripeElement>
      ) : null}
    </div>
  );
};

export default UpdatePaymentMethod;
